import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { listBookings, getBooking, updateBookingStatus, deleteBooking, fetchStats, createManualBooking, updateBookingDetails, clearAdminToken } from "../lib/api";
import AdminPosts from "./AdminPosts";
import AdminClients from "./AdminClients";
import AdminCalendar from "./AdminCalendar";
import AdminEarnings from "./AdminEarnings";
import { ARTISTS } from "../lib/artists";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { MoreHorizontal, RefreshCw, ArrowLeft, Trash2, Eye, Plus, Pencil, Camera } from "lucide-react";
import { toPng } from "html-to-image";

const STATUS_OPTIONS = ["new", "contacted", "scheduled", "archived_done", "archived_cancelled"];
const TATTOO_ARTISTS = ARTISTS.filter((a) => a.role !== "Management");
const STATUS_LABELS = {
  new: "New",
  contacted: "Contacted",
  scheduled: "Scheduled",
  archived_done: "Archived Done",
  archived_cancelled: "Archived Cancelled",
  archived: "Archived Done",
  consulta: "Consulta",
  no_book: "No Book",
};

const formatDate = (d) => {
  try {
    return new Date(d).toLocaleString("en-AU", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return d;
  }
};

export default function Admin() {
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, scheduled: 0, archived: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [scheduleFor, setScheduleFor] = useState(null);
  const [scheduleDates, setScheduleDates] = useState([""]);
  const [scheduleArtist, setScheduleArtist] = useState("");
  const [payFor, setPayFor] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [paySendEmail, setPaySendEmail] = useState(true);
  const [cancelFor, setCancelFor] = useState(null);
  const [cancelSendEmail, setCancelSendEmail] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", artist: "", source: "", email: "", date: "" });
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", artist: "", source: "" });
  const [captureFor, setCaptureFor] = useState(null);

  useEffect(() => {
    document.title = "Admin · JAY — INK ACADEMY";
    refresh();
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([listBookings(), fetchStats()]);
      setBookings(b);
      setStats(s);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        clearAdminToken();
        toast.error("Session expired — please sign in again");
        window.location.reload();
        return;
      }
      const detail = e?.response?.data?.detail;
      toast.error(status ? `Failed to load bookings (${status}${detail ? `: ${detail}` : ""})` : "Failed to load bookings — cannot reach server");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (filter === "all") return bookings;
    if (filter === "archived_done") return bookings.filter((b) => b.status === "archived_done" || b.status === "archived");
    return bookings.filter((b) => b.status === filter);
  }, [bookings, filter]);

  const onStatusChange = async (id, status, extra = {}) => {
    try {
      await updateBookingStatus(id, status, extra);
      toast.success(`Marked ${STATUS_LABELS[status] || status}`);
      refresh();
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleStatusSelect = (b, s) => {
    if (s === "scheduled") {
      setScheduleFor(b);
      setScheduleDates(b.scheduled_dates?.length ? b.scheduled_dates : [b.scheduled_date || ""]);
      setScheduleArtist(b.scheduled_artist || b.preferred_artist || "");
    } else if (s === "archived_done") {
      setPayFor(b);
      setPayAmount(b.amount_charged ? String(b.amount_charged) : "");
      setPaySendEmail(true);
    } else if (s === "archived_cancelled") {
      setCancelFor(b);
      setCancelSendEmail(true);
    } else {
      onStatusChange(b.id, s);
    }
  };

  const setSessionCount = (n) => {
    setScheduleDates((cur) => Array.from({ length: n }, (_, i) => cur[i] || ""));
  };

  const confirmSchedule = async () => {
    const dates = scheduleDates.map((d) => d.trim()).filter(Boolean);
    if (dates.length !== scheduleDates.length) { toast.error("Pick a date for every session"); return; }
    if (!scheduleArtist) { toast.error("Pick an artist"); return; }
    await onStatusChange(scheduleFor.id, "scheduled", {
      scheduled_date: dates[0],
      scheduled_dates: dates,
      scheduled_artist: scheduleArtist,
    });
    setScheduleFor(null);
  };

  const confirmPayment = async () => {
    const amount = parseFloat(payAmount);
    if (isNaN(amount) || amount < 0) { toast.error("Enter a valid amount"); return; }
    await onStatusChange(payFor.id, "archived_done", { amount_charged: amount, send_email: paySendEmail });
    setPayFor(null);
  };

  const confirmCancel = async () => {
    await onStatusChange(cancelFor.id, "archived_cancelled", { send_email: cancelSendEmail });
    setCancelFor(null);
  };

  const openBooking = async (b) => {
    setSelected({ ...b, references: [], referencesLoading: b.references_count > 0 });
    if (!b.references_count) return;
    try {
      const full = await getBooking(b.id);
      setSelected((cur) => (cur?.id === b.id ? { ...cur, references: full.references || [], referencesLoading: false } : cur));
    } catch {
      setSelected((cur) => (cur?.id === b.id ? { ...cur, referencesLoading: false } : cur));
      toast.error("Could not load reference images");
    }
  };

  const saveBookingImage = async (b) => {
    try {
      toast.info("Generating image…");
      setCaptureFor(b);
      await new Promise((r) => setTimeout(r, 60));
      const node = document.getElementById("booking-capture-card");
      if (!node) throw new Error("capture node missing");
      await Promise.all(
        [...node.querySelectorAll("img")].map((im) => im.decode().catch(() => {}))
      );
      const dataUrl = await toPng(node, {
        backgroundColor: "#ffffff",
        pixelRatio: 2,
        width: node.offsetWidth,
        height: node.offsetHeight,
      });
      setCaptureFor(null);
      const filename = `booking-${(b.name || "client").replace(/\s+/g, "-").toLowerCase()}.png`;
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], filename, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Booking — ${b.name}` });
      } else {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = filename;
        a.click();
      }
      toast.success("Image saved");
    } catch (e) {
      setCaptureFor(null);
      if (e?.name !== "AbortError") toast.error("Could not save image");
    }
  };

  const confirmAddBooking = async () => {
    if (!addForm.name.trim()) { toast.error("Enter the client name"); return; }
    if (!addForm.artist) { toast.error("Pick an artist"); return; }
    if (!addForm.source.trim()) { toast.error("Enter where the booking came from"); return; }
    try {
      await createManualBooking({
        name: addForm.name.trim(),
        artist: addForm.artist,
        source: addForm.source.trim(),
        email: addForm.email.trim(),
        scheduled_date: addForm.date || null,
      });
      toast.success("Booking added");
      setAddOpen(false);
      setAddForm({ name: "", artist: "", source: "", email: "", date: "" });
      refresh();
    } catch {
      toast.error("Failed to add booking");
    }
  };

  const openEdit = (b) => {
    setEditFor(b);
    setEditForm({ name: b.name || "", artist: b.preferred_artist || "", source: b.source || "Web" });
  };

  const confirmEdit = async () => {
    if (!editForm.name.trim()) { toast.error("Enter the client name"); return; }
    try {
      await updateBookingDetails(editFor.id, {
        name: editForm.name.trim(),
        artist: editForm.artist || null,
        source: editForm.source.trim() || null,
      });
      toast.success("Booking updated");
      setEditFor(null);
      refresh();
    } catch {
      toast.error("Failed to update booking");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this booking permanently?")) return;
    try {
      await deleteBooking(id);
      toast.success("Booking deleted");
      setSelected(null);
      refresh();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A]">
      {/* Header */}
      <header className="border-b border-[#E5E5E5] bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-10 py-5 flex items-center justify-between gap-3 min-w-0">
          <div className="flex items-center gap-3 md:gap-6 min-w-0 flex-1">
            <Link to="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase hover:opacity-60 shrink-0">
              <ArrowLeft size={14} /> <span className="hidden sm:inline">Site</span>
            </Link>
            <h1 className="font-display text-lg md:text-xl tracking-[0.2em] uppercase shrink-0">
              Admin
            </h1>
            <nav className="flex gap-4 overflow-x-auto whitespace-nowrap flex-1 min-w-0 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <button
                onClick={() => setTab("bookings")}
                data-testid="admin-tab-bookings"
                className={`text-xs tracking-[0.25em] uppercase pb-1 border-b ${tab === "bookings" ? "border-[#0A0A0A]" : "border-transparent text-[#999] hover:text-[#0A0A0A]"}`}
              >
                Bookings
              </button>
              <button
                onClick={() => setTab("calendar")}
                data-testid="admin-tab-calendar"
                className={`text-xs tracking-[0.25em] uppercase pb-1 border-b ${tab === "calendar" ? "border-[#0A0A0A]" : "border-transparent text-[#999] hover:text-[#0A0A0A]"}`}
              >
                Calendar
              </button>
              <button
                onClick={() => setTab("clients")}
                data-testid="admin-tab-clients"
                className={`text-xs tracking-[0.25em] uppercase pb-1 border-b ${tab === "clients" ? "border-[#0A0A0A]" : "border-transparent text-[#999] hover:text-[#0A0A0A]"}`}
              >
                Clients
              </button>
              <button
                onClick={() => setTab("earnings")}
                data-testid="admin-tab-earnings"
                className={`text-xs tracking-[0.25em] uppercase pb-1 border-b ${tab === "earnings" ? "border-[#0A0A0A]" : "border-transparent text-[#999] hover:text-[#0A0A0A]"}`}
              >
                Earnings
              </button>
              <button
                onClick={() => setTab("posts")}
                data-testid="admin-tab-posts"
                className={`text-xs tracking-[0.25em] uppercase pb-1 border-b ${tab === "posts" ? "border-[#0A0A0A]" : "border-transparent text-[#999] hover:text-[#0A0A0A]"}`}
              >
                Journal
              </button>
            </nav>
          </div>
          {tab === "bookings" && (
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setAddOpen(true)}
                data-testid="admin-add-booking"
                className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase bg-[#0A0A0A] text-white px-3 md:px-4 py-2 hover:bg-[#333] transition-colors"
              >
                <Plus size={14} /> <span className="hidden sm:inline">Add Booking</span>
              </button>
              <button
                onClick={refresh}
                data-testid="admin-refresh"
                className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase border border-[#0A0A0A] px-3 md:px-4 py-2 hover:bg-[#0A0A0A] hover:text-white transition-colors shrink-0"
              >
                <RefreshCw size={14} /> <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>
          )}
        </div>
      </header>

      {tab === "posts" ? (
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
          <AdminPosts />
        </div>
      ) : tab === "clients" ? (
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
          <AdminClients />
        </div>
      ) : tab === "calendar" ? (
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
          <AdminCalendar bookings={bookings} />
        </div>
      ) : tab === "earnings" ? (
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
          <AdminEarnings />
        </div>
      ) : (
      <>
      {/* Stats */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {[
            { k: "total", l: "Total" },
            { k: "new", l: "New" },
            { k: "contacted", l: "Contacted" },
            { k: "scheduled", l: "Scheduled" },
            { k: "archived_done", l: "Archived Done" },
            { k: "archived_cancelled", l: "Archived Cancelled" },
          ].map((s) => (
            <button
              key={s.k}
              data-testid={`admin-stat-${s.k}`}
              onClick={() => setFilter(s.k === "total" ? "all" : s.k)}
              className={`text-left bg-white border p-5 transition-colors ${
                (filter === "all" && s.k === "total") || filter === s.k
                  ? "border-[#0A0A0A]"
                  : "border-[#E5E5E5] hover:border-[#0A0A0A]"
              }`}
            >
              <div className="overline mb-2">{s.l}</div>
              <div className="font-display text-3xl md:text-4xl">{stats[s.k] ?? 0}</div>
            </button>
          ))}
        </div>
      </section>

      {/* Table */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-20">
        <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
          <Table data-testid="admin-bookings-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="overline">Date</TableHead>
                <TableHead className="overline">Name</TableHead>
                <TableHead className="overline">Contact</TableHead>
                <TableHead className="overline">Artist</TableHead>
                <TableHead className="overline">Placement / Size</TableHead>
                <TableHead className="overline">Status</TableHead>
                <TableHead className="overline">Source</TableHead>
                <TableHead className="overline text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10 text-[#666]">Loading…</TableCell>
                </TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10 text-[#666]" data-testid="admin-empty">
                    No bookings yet.
                  </TableCell>
                </TableRow>
              )}
              {!loading && filtered.map((b) => (
                <TableRow key={b.id} data-testid={`admin-row-${b.id}`}>
                  <TableCell className="text-sm">{formatDate(b.created_at)}</TableCell>
                  <TableCell>
                    <div className="font-medium">{b.name}</div>
                    <div className="text-xs text-[#666]">{b.instagram || "—"}</div>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div>{b.email}</div>
                    <div className="text-xs text-[#666]">{b.phone}</div>
                  </TableCell>
                  <TableCell className="text-sm">{b.preferred_artist}</TableCell>
                  <TableCell className="text-sm">
                    <div>{b.placement}</div>
                    <div className="text-xs text-[#666]">{b.size}</div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className="rounded-none border-[#0A0A0A] text-[10px] tracking-[0.18em] uppercase"
                    >
                      {STATUS_LABELS[b.status] || b.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm" data-testid={`admin-source-${b.id}`}>
                    {b.source || "Web"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <button
                            onClick={() => openBooking(b)}
                            data-testid={`admin-view-${b.id}`}
                            className="p-2 hover:bg-[#FAFAFA]"
                          >
                            <Eye size={14} />
                          </button>
                        </DialogTrigger>
                        {selected?.id === b.id && (
                          <DialogContent className="max-w-2xl rounded-none border-[#0A0A0A] max-h-[85vh] overflow-y-auto" data-testid={`admin-booking-modal-${b.id}`}>
                            <DialogHeader>
                              <DialogTitle className="font-display text-2xl tracking-tight">
                                {b.name}
                              </DialogTitle>
                              <DialogDescription className="sr-only">Booking details</DialogDescription>
                            </DialogHeader>
                            <button
                              onClick={() => saveBookingImage(selected)}
                              disabled={selected.referencesLoading}
                              data-testid={`save-booking-image-${b.id}`}
                              className="inline-flex items-center justify-center gap-2 w-full border border-[#0A0A0A] py-2.5 text-xs tracking-[0.25em] uppercase hover:bg-[#0A0A0A] hover:text-white transition-colors"
                            >
                              <Camera size={14} /> Save as Image
                            </button>
                            <div className="space-y-4 text-sm">
                              <Row label="Submitted">{formatDate(b.created_at)}</Row>
                              <Row label="Email">{b.email}</Row>
                              <Row label="Phone">{b.phone}</Row>
                              <Row label="Instagram">{b.instagram || "—"}</Row>
                              <Row label="Preferred Artist">{b.preferred_artist}</Row>
                              {b.scheduled_date && (
                                <Row label={b.scheduled_dates?.length > 1 ? `Sessions (${b.scheduled_dates.length})` : "Appointment"}>
                                  <span data-testid={`admin-sessions-${b.id}`}>{(b.scheduled_dates?.length ? b.scheduled_dates : [b.scheduled_date]).join(" · ")}</span>
                                </Row>
                              )}
                              <Row label="Placement">{b.placement}</Row>
                              <Row label="Size">{b.size}</Row>
                              <Row label="Idea">
                                <p className="whitespace-pre-wrap leading-relaxed">{b.idea}</p>
                              </Row>
                              {selected.referencesLoading && (
                                <div className="text-xs text-[#888]" data-testid={`admin-refs-loading-${b.id}`}>Loading references…</div>
                              )}
                              {selected.references && selected.references.length > 0 && (
                                <div>
                                  <div className="overline mb-3">References</div>
                                  <div className="grid grid-cols-4 gap-2">
                                    {selected.references.map((r, i) => (
                                      <a
                                        key={i}
                                        href={r.data_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`admin-ref-${b.id}-${i}`}
                                        className="aspect-square block bg-[#F5F5F5] overflow-hidden"
                                      >
                                        <img src={r.data_url} alt={r.name} className="w-full h-full object-cover" />
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <div className="flex justify-between items-center pt-6 border-t border-[#E5E5E5]">
                                <button
                                  onClick={() => onDelete(b.id)}
                                  data-testid={`admin-delete-${b.id}`}
                                  className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-red-700 hover:underline"
                                >
                                  <Trash2 size={14} /> Delete
                                </button>
                              </div>
                            </div>
                          </DialogContent>
                        )}
                      </Dialog>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            data-testid={`admin-actions-${b.id}`}
                            className="p-2 hover:bg-[#FAFAFA]"
                          >
                            <MoreHorizontal size={14} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-none">
                          <DropdownMenuItem
                            onClick={() => openEdit(b)}
                            data-testid={`admin-edit-${b.id}`}
                            className="text-xs tracking-[0.18em] uppercase rounded-none"
                          >
                            <Pencil size={12} className="mr-1" /> Edit Booking
                          </DropdownMenuItem>
                          {STATUS_OPTIONS.map((s) => (
                            <DropdownMenuItem
                              key={s}
                              onClick={() => handleStatusSelect(b, s)}
                              data-testid={`admin-set-${s}-${b.id}`}
                              className="text-xs tracking-[0.18em] uppercase rounded-none"
                            >
                              Mark {STATUS_LABELS[s] || s}
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuItem
                            onClick={() => onDelete(b.id)}
                            data-testid={`admin-menu-delete-${b.id}`}
                            className="text-xs tracking-[0.18em] uppercase rounded-none text-red-700 focus:text-red-700 border-t border-[#EFEFEF] mt-1"
                          >
                            <Trash2 size={12} className="mr-1" /> Delete Booking
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>
      </>
      )}

      {/* Schedule mini-menu */}
      <Dialog open={!!scheduleFor} onOpenChange={(o) => !o && setScheduleFor(null)}>
        <DialogContent className="max-w-sm rounded-none border-[#0A0A0A]" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight">Schedule Appointment</DialogTitle>
            <DialogDescription className="text-xs text-[#666]">
              {scheduleFor?.name} — pick the artist and the session dates. Big projects can be split into several days.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <div>
              <label className="overline block mb-2">Sessions</label>
              <div className="grid grid-cols-4 gap-2" data-testid="schedule-session-count">
                {[1, 2, 3, 4].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setSessionCount(n)}
                    data-testid={`schedule-sessions-${n}`}
                    className={`py-2 text-xs tracking-[0.2em] uppercase border transition-colors ${scheduleDates.length === n ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] hover:border-[#0A0A0A]"}`}
                  >
                    {n} {n === 1 ? "day" : "days"}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              {scheduleDates.map((d, i) => (
                <div key={i}>
                  <label className="overline block mb-2">{scheduleDates.length > 1 ? `Session ${i + 1}` : "Date"}</label>
                  <input
                    type="date"
                    data-testid={i === 0 ? "schedule-date-input" : `schedule-date-input-${i + 1}`}
                    value={d}
                    min={i > 0 ? scheduleDates[i - 1] || undefined : undefined}
                    onChange={(e) => setScheduleDates((cur) => cur.map((x, j) => (j === i ? e.target.value : x)))}
                    className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#0A0A0A]"
                  />
                </div>
              ))}
            </div>
            <div>
              <label className="overline block mb-2">Artist</label>
              <select
                data-testid="schedule-artist-select"
                value={scheduleArtist}
                onChange={(e) => setScheduleArtist(e.target.value)}
                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm bg-white cursor-pointer focus:outline-none focus:border-[#0A0A0A]"
              >
                <option value="">Select artist…</option>
                {TATTOO_ARTISTS.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
            <button
              onClick={confirmSchedule}
              data-testid="schedule-confirm"
              className="w-full bg-[#0A0A0A] text-white py-3 text-xs tracking-[0.28em] uppercase hover:bg-[#333] transition-colors"
            >
              Confirm Appointment{scheduleDates.length > 1 ? `s (${scheduleDates.length} days)` : ""}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Archived Cancelled confirm */}
      <Dialog open={!!cancelFor} onOpenChange={(o) => !o && setCancelFor(null)}>
        <DialogContent className="max-w-sm rounded-none border-[#0A0A0A]" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight">Archive as Cancelled</DialogTitle>
            <DialogDescription className="text-xs text-[#666]">
              {cancelFor?.name} — this booking will be archived as cancelled.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <EmailToggle value={cancelSendEmail} onChange={setCancelSendEmail} testId="cancel-email" label="Send the “restart your booking” email to the client?" disabled={!cancelFor?.email} />
            <button
              onClick={confirmCancel}
              data-testid="cancel-confirm"
              className="w-full bg-[#0A0A0A] text-white py-3 text-xs tracking-[0.28em] uppercase hover:bg-[#333] transition-colors"
            >
              {cancelSendEmail && cancelFor?.email ? "Archive & Send Email" : "Archive Without Email"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment mini-menu (Archived Done) */}
      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent className="max-w-sm rounded-none border-[#0A0A0A]" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight">Complete Appointment</DialogTitle>
            <DialogDescription className="text-xs text-[#666]">
              {payFor?.name} — enter what the artist charged the client.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <div>
              <label className="overline block mb-2">Amount Charged (AUD)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                data-testid="payment-amount-input"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#0A0A0A]"
              />
            </div>
            <div className="border border-[#E5E5E5] bg-[#FAFAFA] p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#666]">Studio commission (30%)</span>
                <span data-testid="payment-commission" className="font-medium">
                  ${((parseFloat(payAmount) || 0) * 0.3).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#666]">Artist earnings (70%)</span>
                <span data-testid="payment-artist-net" className="font-medium">
                  ${((parseFloat(payAmount) || 0) * 0.7).toFixed(2)}
                </span>
              </div>
            </div>
            <EmailToggle value={paySendEmail} onChange={setPaySendEmail} testId="payment-email" label="Send the aftercare email to the client?" disabled={!payFor?.email} />
            <button
              onClick={confirmPayment}
              data-testid="payment-confirm"
              className="w-full bg-[#0A0A0A] text-white py-3 text-xs tracking-[0.28em] uppercase hover:bg-[#333] transition-colors"
            >
              {paySendEmail && payFor?.email ? "Archive Done & Send Email" : "Archive Done Without Email"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Booking mini-menu */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm rounded-none border-[#0A0A0A] max-h-[85vh] overflow-y-auto" data-testid="add-booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight">Add Booking</DialogTitle>
            <DialogDescription className="text-xs text-[#666]">
              Add a booking that didn't come through the website form.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="overline block mb-2">Client Name *</label>
              <input
                data-testid="add-booking-name"
                value={addForm.name}
                onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Sarah Thompson"
                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#0A0A0A]"
              />
            </div>
            <div>
              <label className="overline block mb-2">Artist *</label>
              <select
                data-testid="add-booking-artist"
                value={addForm.artist}
                onChange={(e) => setAddForm((f) => ({ ...f, artist: e.target.value }))}
                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm bg-white cursor-pointer focus:outline-none focus:border-[#0A0A0A]"
              >
                <option value="">Select artist…</option>
                {TATTOO_ARTISTS.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Source *</label>
              <input
                data-testid="add-booking-source"
                value={addForm.source}
                onChange={(e) => setAddForm((f) => ({ ...f, source: e.target.value }))}
                placeholder="e.g. Instagram, Gmail, Walk-in…"
                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#0A0A0A]"
              />
            </div>
            <div>
              <label className="overline block mb-2">Email <span className="normal-case tracking-normal text-[#999]">(optional)</span></label>
              <input
                type="email"
                data-testid="add-booking-email"
                value={addForm.email}
                onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="client@email.com"
                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#0A0A0A]"
              />
            </div>
            <div>
              <label className="overline block mb-2">Appointment Date <span className="normal-case tracking-normal text-[#999]">(optional — shows on calendar)</span></label>
              <input
                type="date"
                data-testid="add-booking-date"
                value={addForm.date}
                onChange={(e) => setAddForm((f) => ({ ...f, date: e.target.value }))}
                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#0A0A0A]"
              />
            </div>
            <button
              onClick={confirmAddBooking}
              data-testid="add-booking-confirm"
              className="w-full bg-[#0A0A0A] text-white py-3 text-xs tracking-[0.28em] uppercase hover:bg-[#333] transition-colors"
            >
              Add Booking
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Booking mini-menu */}
      <Dialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)}>
        <DialogContent className="max-w-sm rounded-none border-[#0A0A0A]" data-testid="edit-booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight">Edit Booking</DialogTitle>
            <DialogDescription className="text-xs text-[#666]">
              Update the client name, assigned artist or booking source.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="overline block mb-2">Client Name</label>
              <input
                data-testid="edit-booking-name"
                value={editForm.name}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#0A0A0A]"
              />
            </div>
            <div>
              <label className="overline block mb-2">Artist</label>
              <select
                data-testid="edit-booking-artist"
                value={editForm.artist}
                onChange={(e) => setEditForm((f) => ({ ...f, artist: e.target.value }))}
                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm bg-white cursor-pointer focus:outline-none focus:border-[#0A0A0A]"
              >
                <option value="">Select artist…</option>
                {TATTOO_ARTISTS.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Source</label>
              <input
                data-testid="edit-booking-source"
                value={editForm.source}
                onChange={(e) => setEditForm((f) => ({ ...f, source: e.target.value }))}
                placeholder="e.g. Web, Instagram, Gmail…"
                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#0A0A0A]"
              />
            </div>
            <button
              onClick={confirmEdit}
              data-testid="edit-booking-confirm"
              className="w-full bg-[#0A0A0A] text-white py-3 text-xs tracking-[0.28em] uppercase hover:bg-[#333] transition-colors"
            >
              Save Changes
            </button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Off-screen capture card for Save as Image */}
      {captureFor && (
        <div style={{ position: "fixed", top: 0, left: "-10000px", zIndex: -1 }}>
          <div
            id="booking-capture-card"
            style={{ width: 760, background: "#ffffff", padding: 48, color: "#0A0A0A", fontFamily: "'Jost', 'Futura', 'Helvetica Neue', sans-serif" }}
          >
            <div style={{ fontSize: 11, letterSpacing: "0.3em", textTransform: "uppercase", color: "#999", marginBottom: 10 }}>
              JAY-INK ACADEMY — Booking
            </div>
            <div style={{ fontSize: 30, fontWeight: 500, marginBottom: 24 }}>{captureFor.name}</div>
            {[
              ["Submitted", formatDate(captureFor.created_at)],
              ["Email", captureFor.email || "—"],
              ["Phone", captureFor.phone || "—"],
              ["Instagram", captureFor.instagram || "—"],
              ["Artist", captureFor.scheduled_artist || captureFor.preferred_artist || "—"],
              ["Placement", captureFor.placement || "—"],
              ["Size", captureFor.size || "—"],
              ["Status", STATUS_LABELS[captureFor.status] || captureFor.status],
              ["Source", captureFor.source || "Web"],
              ...(captureFor.scheduled_dates?.length > 1
                ? [["Sessions", captureFor.scheduled_dates.join("  ·  ")]]
                : captureFor.scheduled_date ? [["Appointment", captureFor.scheduled_date]] : []),
            ].map(([label, value]) => (
              <div key={label} style={{ display: "flex", borderBottom: "1px solid #F0F0F0", padding: "10px 0" }}>
                <div style={{ width: 190, flexShrink: 0, fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase", color: "#666", paddingTop: 3 }}>
                  {label}
                </div>
                <div style={{ fontSize: 15, fontWeight: 300 }}>{value}</div>
              </div>
            ))}
            <div style={{ display: "flex", padding: "10px 0" }}>
              <div style={{ width: 190, flexShrink: 0, fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase", color: "#666", paddingTop: 3 }}>
                Idea
              </div>
              <div style={{ fontSize: 15, fontWeight: 300, lineHeight: 1.7, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {(captureFor.idea || "—").trim()}
              </div>
            </div>
            {captureFor.references && captureFor.references.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase", color: "#666", marginBottom: 10 }}>
                  References
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                  {captureFor.references.map((r, i) => (
                    <img key={i} src={r.data_url} alt="" style={{ width: "100%", aspectRatio: "1", objectFit: "cover" }} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-4 py-2 border-b border-[#F0F0F0]">
      <div className="overline">{label}</div>
      <div className="col-span-2">{children}</div>
    </div>
  );
}


function EmailToggle({ value, onChange, testId, label, disabled }) {
  return (
    <div className="border border-[#E5E5E5] bg-[#FAFAFA] p-4 space-y-3" data-testid={testId}>
      <div className="text-xs text-[#444]">{disabled ? "This booking has no email address — no email will be sent." : label}</div>
      {!disabled && (
        <div className="grid grid-cols-2 gap-2">
          {[["yes", "Yes, send email", true], ["no", "No, don't send", false]].map(([k, l, v]) => (
            <button
              key={k}
              type="button"
              onClick={() => onChange(v)}
              data-testid={`${testId}-${k}`}
              className={`py-2 text-[10px] tracking-[0.2em] uppercase border transition-colors ${value === v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#E5E5E5] hover:border-[#0A0A0A]"}`}
            >
              {l}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
